import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, Copy, Loader2, Plus, RotateCw, Trash2, Zap } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Hook {
  id: string;
  name: string;
  branch: string;
  createdByUserId: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface DeploymentRow {
  branch: string;
}

/**
 * T022 — Deploy hooks UI. Lists active + revoked hooks; create form lets
 * the operator pick from the project's known branches (derived from recent
 * deployments) or type a new one. Token URL is shown ONCE on create + on
 * regenerate; we never see the raw token again.
 */
export function DeployHooks() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [revealedUrl, setRevealedUrl] = useState<{ hookId: string; url: string } | null>(null);

  const hooks = useQuery({
    queryKey: ["deploy-hooks", org, project],
    queryFn: () => api.get<{ hooks: Hook[] }>(`/orgs/${org}/projects/${project}/deploy-hooks`).then((r) => r.hooks),
  });

  // Pull recent deployments to seed the branch picker — most operators
  // want to fire a hook against a branch they've already deployed once.
  const deployments = useQuery({
    queryKey: ["deployments", org, project],
    queryFn: () =>
      api
        .get<{ deployments: DeploymentRow[] }>(`/orgs/${org}/projects/${project}/deployments?limit=50`)
        .then((r) => r.deployments),
  });
  const knownBranches = useMemo(() => {
    const s = new Set<string>();
    for (const d of deployments.data ?? []) s.add(d.branch);
    return Array.from(s).sort();
  }, [deployments.data]);

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/projects/${project}/deploy-hooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deploy-hooks", org, project] }),
  });
  const regenerate = useMutation({
    mutationFn: (id: string) =>
      api.post<{ url: string }>(`/orgs/${org}/projects/${project}/deploy-hooks/${id}/regenerate`),
    onSuccess: (r, id) => {
      setRevealedUrl({ hookId: id, url: r.url });
      qc.invalidateQueries({ queryKey: ["deploy-hooks", org, project] });
    },
  });

  return (
    <div className="space-y-4">
      <CreateHookForm
        orgSlug={org!}
        projectSlug={project!}
        knownBranches={knownBranches}
        onCreated={(id, url) => {
          setRevealedUrl({ hookId: id, url });
          qc.invalidateQueries({ queryKey: ["deploy-hooks", org, project] });
        }}
      />
      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-muted-foreground" /> Deploy hooks
          </h3>
          <span className="text-2xs text-subtle-foreground">{hooks.data?.length ?? 0}</span>
        </div>
        {hooks.isLoading && <Empty>Loading…</Empty>}
        {!hooks.isLoading && (hooks.data?.length ?? 0) === 0 && (
          <Empty>
            No hooks yet. Create one above to get a stable URL your CI can <span className="font-mono">curl -X POST</span>.
          </Empty>
        )}
        {hooks.data?.map((h) => (
          <div key={h.id} className="of-row gap-3 items-start">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold truncate">{h.name}</span>
                <span className="text-2xs uppercase tracking-wider bg-subtle border border-border-muted rounded px-1.5 py-0.5 font-mono">
                  {h.branch}
                </span>
                {h.revokedAt && (
                  <span className="text-2xs uppercase tracking-wider bg-danger-subtle text-danger border border-danger/30 rounded px-1.5 py-0.5">
                    revoked
                  </span>
                )}
              </div>
              <div className="text-2xs text-subtle-foreground mt-0.5">
                Created {new Date(h.createdAt).toLocaleDateString()}
                {h.lastUsedAt && <> · Last fired {relative(h.lastUsedAt)}</>}
              </div>
              {revealedUrl?.hookId === h.id && (
                <CopyableUrl url={revealedUrl.url} onClose={() => setRevealedUrl(null)} />
              )}
            </div>
            {!h.revokedAt && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (confirm("Regenerate URL? The old URL will return 410 immediately.")) regenerate.mutate(h.id);
                  }}
                  disabled={regenerate.isPending}
                  title="Regenerate URL"
                >
                  {regenerate.isPending && regenerate.variables === h.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-danger"
                  onClick={() => {
                    if (confirm(`Revoke '${h.name}'? Returns 410 forever.`)) revoke.mutate(h.id);
                  }}
                  disabled={revoke.isPending}
                >
                  {revoke.isPending && revoke.variables === h.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateHookForm({
  orgSlug,
  projectSlug,
  knownBranches,
  onCreated,
}: {
  orgSlug: string;
  projectSlug: string;
  knownBranches: string[];
  onCreated: (id: string, url: string) => void;
}) {
  const [name, setName] = useState("");
  const [branch, setBranch] = useState(knownBranches[0] ?? "main");
  const create = useMutation({
    mutationFn: () =>
      api.post<{ hook: { id: string; name: string; branch: string }; url: string }>(
        `/orgs/${orgSlug}/projects/${projectSlug}/deploy-hooks`,
        { name, branch },
      ),
    onSuccess: (r) => {
      onCreated(r.hook.id, r.url);
      setName("");
    },
  });
  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Create hook
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-2">
          <div className="space-y-1">
            <Label htmlFor="hook-name" className="text-xs">Name</Label>
            <Input
              id="hook-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="release-script"
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">a-z 0-9 _ - (max 64)</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="hook-branch" className="text-xs">Branch</Label>
            <Input
              id="hook-branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              list="known-branches"
              placeholder="main"
              className="font-mono text-sm"
            />
            <datalist id="known-branches">
              {knownBranches.map((b) => <option key={b} value={b} />)}
            </datalist>
          </div>
        </div>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate()}
            disabled={!name || !branch || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Create
          </Button>
        </div>
      </div>
    </details>
  );
}

function CopyableUrl({ url, onClose }: { url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 rounded-md border border-success/30 bg-success-subtle/50 p-3 space-y-2">
      <div className="text-2xs font-semibold">Hook URL (shown ONCE — copy now)</div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={url}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 min-w-0 h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
            } catch {
              const ta = document.createElement("textarea");
              ta.value = url;
              document.body.appendChild(ta);
              ta.select();
              document.execCommand("copy");
              document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
      <div className="text-2xs text-muted-foreground">
        Test it: <span className="font-mono break-all">curl -X POST &quot;{url}&quot;</span>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
